import { ReactNode, useState } from "react";
import { Link, useLocation } from "wouter";
import { ChevronRight, ArrowLeft } from "lucide-react";
import { TopNav } from "./TopNav";
import { getSession, getCurrentTenant } from "@/lib/session";
import { useTour } from "@/lib/tour-store";
import { ImpersonationBanner } from "./ImpersonationBanner";
import { ImpersonationPicker } from "./ImpersonationPicker";
import { getImpersonationTarget } from "@/lib/impersonation";
import { AIP_VERSION, AIP_QUESTION_BANK_VERSION, AIP_SPEC_VERSION } from "@/version";

/** Return role-aware home path for logo click — same logic as AccountScorecard and Mi6BrandBand. */
function roleDashboardPath(): string {
  const s = getSession();
  if (!s) return "/";
  switch (s.role) {
    case "ae":        return "/dashboards/ae";
    case "bdr":       return "/dashboards/bdr";
    case "manager":   return s.isDualRole ? "/dashboards/executive" : "/dashboards/manager";
    case "executive": return "/dashboards/executive";
    case "marketing": return "/dashboards/marketing";
    case "se":        return "/dashboards/se";
    default:          return "/dashboards";
  }
}

const VIEW_AS_OPTIONS: { label: string; path: string }[] = [
  { label: "Executive", path: "/dashboards/executive" },
  { label: "Manager",   path: "/dashboards/manager" },
  { label: "AE",        path: "/dashboards/ae" },
  { label: "BDR",       path: "/dashboards/bdr" },
  { label: "SE",        path: "/dashboards/se" },
  { label: "Marketing", path: "/dashboards/marketing" },
  { label: "Reports",   path: "/reporting" },
];

/**
 * D-074 (2026-05-22) replaces the old ViewAsSwitcher dropdown. The old
 * dropdown just navigated between role dashboards while leaving the
 * authenticated user unchanged — so a manager would see manager data on the
 * BDR layout, not what the BDR actually sees. The new button opens a picker
 * that starts a true impersonation session: every API request carries the
 * target's user id, so the data the manager sees is identical to what the
 * target rep would see if they logged in themselves.
 *
 * Old VIEW_AS_OPTIONS constant retained (above) only for layout reference;
 * not used in the new flow.
 */
function ImpersonateButton() {
  const u = getSession();
  const [open, setOpen] = useState(false);
  if (!u) return null;
  // Server enforces the same rule. Client gates to keep the UI tidy.
  const canImpersonate = u.role === "manager" || u.role === "executive";
  if (!canImpersonate) return null;

  // Hide the entry point while already impersonating — the red exit banner is
  // the only way out of an active session.
  const alreadyImpersonating = !!getImpersonationTarget();
  if (alreadyImpersonating) return null;

  return (
    <>
      <button
        type="button"
        data-tour="role-switcher"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background text-[#0B0B0F] text-[12px] font-medium px-3 py-1.5 hover:bg-[#F3F4F6] transition-colors whitespace-nowrap"
      >
        <span className="uppercase tracking-[0.14em] text-[10px] text-muted-foreground">View as</span>
        <span>User</span>
      </button>
      <ImpersonationPicker open={open} onClose={() => setOpen(false)} />
    </>
  );
}

// Old VIEW_AS_OPTIONS used by previous dropdown. Kept here for now in case
// any other code imports it; can be removed in a follow-up sweep.
void VIEW_AS_OPTIONS;

function DemoTourButton() {
  const u = getSession();
  const tenant = getCurrentTenant();
  const { startTour, isActive, exitTour } = useTour();

  if (!u) return null;
  // F-02 (2026-05-16): Demo tour is only for the Mi6 Demo tenant. Production
  // customers should never see it.
  if (!tenant?.is_demo) return null;

  if (isActive) {
    return (
      <button
        type="button"
        onClick={() => exitTour()}
        data-tour="demo-tour-button"
        className="inline-flex items-center gap-2 rounded-md border border-[#DC2626] text-[#DC2626] bg-background px-3 py-1.5 text-[12px] font-medium hover:bg-[#DC2626] hover:text-white transition-colors whitespace-nowrap"
      >
        Exit tour
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => startTour()}
      data-tour="demo-tour-button"
      className="inline-flex items-center gap-2 rounded-md border border-[#0B0B0F] text-[#0B0B0F] bg-background px-3 py-1.5 text-[12px] font-medium hover:bg-[#0B0B0F] hover:text-white transition-colors whitespace-nowrap"
    >
      Demo tour
    </button>
  );
}

/** W66.X16 — always-visible guided tour entry point. Links to /#/tour. */
function GuidedTourButton() {
  return (
    <Link
      href="/tour"
      className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background text-[#0B0B0F] text-[12px] font-medium px-3 py-1.5 hover:bg-[#F3F4F6] transition-colors whitespace-nowrap"
    >
      Platform tour
    </Link>
  );
}

export function Header({ subtitle }: { subtitle?: string }) {
  return (
    <header className="border-b border-border bg-[hsl(var(--surface))]">
      <div className="max-w-[1400px] mx-auto px-6 py-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <Link href={roleDashboardPath()} className="flex items-center gap-3" aria-label="Mi6 Group · powered by Attilius">
          <img
              src="brand/mi6-attilius-horizontal.png"
              alt="Mi6 Group · powered by Attilius"
              className="h-7 sm:h-9 w-auto block"
            />
          <span className="hidden md:flex flex-col leading-tight">
            <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              Attilius · {AIP_VERSION}
            </span>
          </span>
        </Link>
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <div className="overflow-x-auto flex-shrink-0 max-w-full [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <TopNav />
          </div>
          <ImpersonateButton />
          <DemoTourButton />
          <GuidedTourButton />
        </div>
      </div>
      {subtitle && (
        <div className="max-w-[1400px] mx-auto px-6 pb-3 text-xs text-muted-foreground">
          {subtitle}
        </div>
      )}
    </header>
  );
}

export function Footer() {
  return (
    <footer className="mt-16 border-t border-border bg-[hsl(var(--surface))]">
      <div className="max-w-[1400px] mx-auto px-6 py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-[11px] text-muted-foreground">
        <span>© 2026 Mi6 Group LLC — Confidential. All intellectual property is the property of Mi6 Group LLC.</span>
        <span className="font-mono">Attilius · Question Bank {AIP_QUESTION_BANK_VERSION} · Spec {AIP_SPEC_VERSION}</span>
      </div>
    </footer>
  );
}

// Top-level routes do not show a Back button or breadcrumb trail.
const TOP_LEVEL_ROUTES: ReadonlySet<string> = new Set([
  "/",
  "/accounts",
  "/sales-path",
  "/question-bank",
  "/helix",
  "/opportunity-dna",
  "/team-dna",
  "/methodology",
  "/flowcharts",
  "/templates",
  "/manuals",
  "/pipeline-dna/me",
  "/tour",
]);

// Map first path segment → human-readable section label for the breadcrumb trail.
const SECTION_LABEL: Record<string, string> = {
  "accounts":      "Accounts",
  "account":       "Accounts",
  "sales-path":    "Sales Path",
  "decision-tree": "Sales Path",
  "question-bank": "Question Bank",
  "question-tree": "Question Bank",
  "helix":         "Opportunity DNA",
  "opportunity-dna": "Opportunity DNA",
  "dna":           "Opportunity DNA",
  "team-dna":      "Team DNA",
  "pipeline-dna":  "Pipeline DNA",
  "methodology":   "Methodology",
  "flowcharts":    "Checkpoint Flowcharts",
  "templates":     "Templates",
  "manuals":       "Manuals",
  "dashboards":    "Dashboards",
  "reporting":     "Reports",
  "settings":      "Settings",
  "library":       "Library",
  "calculator":    "Opportunity Calculator",
  "calculator-v3": "Opportunity Calculator",
  "contacts":      "Contacts",
  "cadence":       "Cadence",
  "tour":           "Platform Tour",
};

function titleize(seg: string): string {
  return seg
    .split("-")
    .map((s) => (s.length === 0 ? s : s[0].toUpperCase() + s.slice(1)))
    .join(" ");
}

function buildCrumbs(loc: string): { href: string; label: string }[] {
  const segs = loc.split("/").filter(Boolean);
  if (segs.length === 0) return [];
  const crumbs: { href: string; label: string }[] = [];
  let acc = "";
  for (let i = 0; i < segs.length; i++) {
    acc += "/" + segs[i];
    const label =
      i === 0
        ? SECTION_LABEL[segs[0]] ?? titleize(segs[0])
        : titleize(decodeURIComponent(segs[i]));
    crumbs.push({ href: acc, label });
  }
  return crumbs;
}

function isTopLevel(loc: string): boolean {
  // Strip query/hash so /dna?account=… still treated as top-level.
  const path = loc.split("?")[0].split("#")[0];
  return TOP_LEVEL_ROUTES.has(path);
}

export function Breadcrumbs({ pageLabel }: { pageLabel?: string }) {
  const [loc] = useLocation();
  if (isTopLevel(loc)) return null;
  const crumbs = buildCrumbs(loc);
  if (crumbs.length === 0) return null;
  if (pageLabel) {
    crumbs[crumbs.length - 1] = { ...crumbs[crumbs.length - 1], label: pageLabel };
  }
  return (
    <nav aria-label="Breadcrumb" className="text-[12px] text-muted-foreground">
      <ol className="flex flex-wrap items-center gap-1">
        <li>
          <Link href="/" className="hover:text-foreground transition-colors">Home</Link>
        </li>
        {crumbs.map((c, i) => (
          <li key={c.href} className="flex items-center gap-1">
            <ChevronRight className="h-3 w-3 opacity-60" aria-hidden />
            {i === crumbs.length - 1 ? (
              <span className="text-foreground" aria-current="page">{c.label}</span>
            ) : (
              <Link href={c.href} className="hover:text-foreground transition-colors">{c.label}</Link>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

export function BackButton({ to, label }: { to?: string; label?: string }) {
  const [loc, setLoc] = useLocation();
  if (isTopLevel(loc)) return null;
  const crumbs = buildCrumbs(loc);
  const parent = crumbs.length >= 2 ? crumbs[crumbs.length - 2] : null;
  const computedLabel = label ?? (parent ? parent.label : "Back");
  const onClick = () => {
    if (to) {
      setLoc(to);
      return;
    }
    if (parent) {
      setLoc(parent.href);
      return;
    }
    if (typeof window !== "undefined" && window.history.length > 1) {
      window.history.back();
    } else {
      setLoc("/");
    }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 text-[12px] text-muted-foreground hover:text-foreground transition-colors"
      aria-label={`Back to ${computedLabel}`}
    >
      <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
      <span>{computedLabel}</span>
    </button>
  );
}

export function PageShell({
  subtitle,
  children,
  pageLabel,
  backTo,
  backLabel,
}: {
  subtitle?: string;
  children: ReactNode;
  pageLabel?: string;
  backTo?: string;
  backLabel?: string;
}) {
  const [loc] = useLocation();
  const showRail = !isTopLevel(loc);
  return (
    <div className="min-h-screen flex flex-col">
      <ImpersonationBanner />
      <Header subtitle={subtitle} />
      {showRail && (
        <div className="border-b border-border bg-[hsl(var(--surface))]">
          <div className="max-w-[1400px] w-full mx-auto px-6 py-2 flex items-center justify-between gap-4">
            <BackButton to={backTo} label={backLabel} />
            <Breadcrumbs pageLabel={pageLabel} />
          </div>
        </div>
      )}
      <main className="flex-1 max-w-[1400px] w-full mx-auto px-6 py-10">{children}</main>
      <Footer />
    </div>
  );
}
